import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCopilot } from '../contexts/CopilotContext';
import config from '../config';
import GoogleIntegrationCard from '../components/GoogleIntegrationCard';
import PaymentDialog from '../components/PaymentDialog';
import ManageFlatsDialog from '../components/ManageFlatsDialog';
import SectionCard from '../components/settings/SectionCard';
import SettingsRow from '../components/settings/SettingsRow';
import { USER_ROLES } from '../userRoles';
import StatusPill, { PillTone } from '../components/settings/StatusPill';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  user_role?: string | null;
  created_at: string;
  last_login: string | null;
  mfa_enabled?: boolean;
  mfa_enrolled_at?: string | null;
  mfa_pending?: boolean;
}

const srOnly = {
  position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
} as const;

const formatDate = (value: string | null | undefined, withTime = false) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(withTime && { hour: '2-digit', minute: '2-digit' }),
  });
};

const capitalise = (name: string) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : '');

const PLAN_BADGES: Record<string, { label: string; tone: PillTone }> = {
  trial: { label: 'Trial', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  past_due: { label: 'Past due', tone: 'warning' },
  cancelled: { label: 'Canceled', tone: 'neutral' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  none: { label: 'None', tone: 'neutral' },
};

const ProfilePage: React.FC = () => {
  const { token, subscription, logout, refreshSubscriptionStatus } = useAuth();
  const { showMessage } = useCopilot();
  const { palette } = useTheme();
  const t = palette.tokens;

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [fixedPlanDialogOpen, setFixedPlanDialogOpen] = useState(false);
  const [manageFlatsOpen, setManageFlatsOpen] = useState(false);

  // Account deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Name edit state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA enrollment state
  const [mfaQrCode, setMfaQrCode] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaConfirming, setMfaConfirming] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  // Re-enrollment (lost device) — gated on re-entering the login password, see handleStartMfaEnroll
  const [mfaResetDialogOpen, setMfaResetDialogOpen] = useState(false);
  const [mfaResetPassword, setMfaResetPassword] = useState('');

  const announce = (message: string) => {
    setAnnouncement('');
    setTimeout(() => setAnnouncement(message), 50);
  };

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await fetch(`${config.apiUrl}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setProfile(data);
      if (data.mfa_pending) fetchPendingMfaQr();
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { refreshSubscriptionStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resumes an enrollment/reset interrupted before the confirm step (tab switch, page reload,
  // browser restart) — re-displays the already-generated pending secret's QR code without
  // requiring the password again, since nothing new is being created here.
  const fetchPendingMfaQr = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/pending-qr`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return; // nothing pending (404) — leave the normal enabled/not-enabled view as-is
      const data = await res.json();
      setMfaQrCode(data.qr_code);
      setMfaSecret(data.secret);
    } catch {
      // Silent — this is a best-effort resume, not a user-initiated action with its own error UI.
    }
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setChangingPassword(true);
      const response = await fetch(`${config.apiUrl}/api/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to change password');

      closePasswordDialog();
      showMessage({ text: 'Password changed successfully.' });
      announce('Password changed');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmed(false);
    setDeletePassword('');
    setDeleteError(null);
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeletingAccount(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/profile`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not delete your account');
      logout();
    } catch (err: any) {
      setDeleteError(err.message || 'Could not delete your account');
      setDeletingAccount(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/payments/create-portal-session`);
      window.location.href = res.data.url;
    } catch {
      showMessage({ text: 'Could not open billing portal. Try again.' });
    } finally {
      setPortalLoading(false);
    }
  };

  const startEditingName = () => {
    setNameValue(profile?.name || '');
    setNameError(null);
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNameError(null);
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) {
      setNameError('Enter your name');
      return;
    }
    if (trimmed === profile?.name) {
      cancelEditingName();
      return;
    }
    setSavingName(true);
    setNameError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/profile`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update name');
      setProfile(prev => prev ? { ...prev, name: data.name } : prev);
      setEditingName(false);
      announce('Name saved');
    } catch (err: any) {
      setNameError(err.message || 'Could not update your name');
    } finally {
      setSavingName(false);
    }
  };

  const [editingRole, setEditingRole] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const handleSaveRole = async (role: string) => {
    setSavingRole(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/profile`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_role: role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      setProfile(prev => prev ? { ...prev, user_role: data.user_role } : prev);
      setEditingRole(false);
      announce('Role saved');
    } catch {
      showMessage({ text: 'Could not update your role. Try again.' });
    } finally {
      setSavingRole(false);
    }
  };

  const handleStartMfaEnroll = async (currentPasswordForReset?: string) => {
    setMfaError(null);
    setMfaEnrolling(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/enroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(currentPasswordForReset ? { current_password: currentPasswordForReset } : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start MFA enrollment');
      setMfaQrCode(data.qr_code);
      setMfaSecret(data.secret);
      setMfaResetDialogOpen(false);
      setMfaResetPassword('');
    } catch (err: any) {
      setMfaError(err.message || (currentPasswordForReset ? 'Failed to reset MFA' : 'Failed to start MFA enrollment'));
    } finally {
      setMfaEnrolling(false);
    }
  };

  const handleConfirmResetPassword = () => {
    if (!mfaResetPassword.trim()) return;
    handleStartMfaEnroll(mfaResetPassword.trim());
  };

  const handleConfirmMfa = async () => {
    if (!mfaConfirmCode.trim()) return;
    setMfaError(null);
    setMfaConfirming(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: mfaConfirmCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setProfile(prev => prev ? { ...prev, mfa_enabled: true, mfa_enrolled_at: new Date().toISOString() } : prev);
      setMfaQrCode(null);
      setMfaSecret(null);
      setMfaConfirmCode('');
      announce('Two-factor authentication enabled');
    } catch (err: any) {
      setMfaError(err.message || 'Invalid code');
    } finally {
      setMfaConfirming(false);
    }
  };

  // ── Billing ────────────────────────────────────────────────────────────────
  const status = subscription?.subscription_status;
  const canManageSubscription = subscription?.can_manage_subscription ?? subscription?.has_payment_method ?? false;
  const scheduledCancellationDate = subscription?.subscription_status === 'active' && subscription?.cancel_at_period_end
    ? formatDate(subscription.scheduled_cancellation_date) || null
    : null;
  const planBadge = status ? (PLAN_BADGES[status] || { label: status.replace('_', ' '), tone: 'neutral' as PillTone }) : null;

  const isPerFlat = subscription?.flat_limit != null && status === 'active';
  const [flatUnitAmount, setFlatUnitAmount] = useState<number | null>(null);
  useEffect(() => {
    if (!isPerFlat || !token) return;
    axios.get(`${config.apiUrl}/api/payments/flat-plan`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setFlatUnitAmount(res.data.unit_amount))
      .catch(() => {});
  }, [isPerFlat, token]);

  type Banner = { tone: 'warning' | 'danger' | 'neutral'; text: string; action: React.ReactNode } | null;
  const isActiveTrial = Boolean(subscription && status === 'trial' && !subscription.trial_expired);
  const trialDaysLeft = subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date).getTime() - Date.now()) / 86400000))
    : null;
  const trialSummary = [
    trialDaysLeft !== null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : null,
    subscription?.certificate_limit ? `${subscription.certificate_count ?? 0} of ${subscription.certificate_limit} certificates used` : null,
  ].filter(Boolean).join(' · ');
  const addPaymentPrimary = Boolean(subscription && status !== 'past_due' && (isActiveTrial || !subscription.has_payment_method));
  const banner: Banner = !subscription ? null
    : status === 'past_due'
      ? {
        tone: 'danger',
        text: 'Your last payment failed. Update your card to keep your plan.',
        action: canManageSubscription
          ? <Button variant="outlined" color="error" onClick={openBillingPortal} disabled={portalLoading}>Update payment method</Button>
          : <Button variant="outlined" color="error" onClick={() => setPaymentDialogOpen(true)}>Update payment method</Button>,
      }
      : isActiveTrial
        ? {
          tone: subscription?.certificate_limit_reached ? 'warning' : 'neutral',
          text: subscription?.certificate_limit_reached
            ? `You have reached your limit of ${subscription.certificate_limit} certificates on the Trial plan. Buy a plan to continue.`
            : `Free trial: ${trialSummary}. Purchase a plan to keep going after your trial.`,
          action: <Button variant="contained" onClick={() => setPaymentDialogOpen(true)}>Purchase a plan</Button>,
        }
      : status !== 'active' && !subscription.has_payment_method
        ? {
          tone: 'warning',
          text: 'No active plan. Purchase a plan to continue.',
          action: <Button variant="contained" onClick={() => setPaymentDialogOpen(true)}>Purchase a plan</Button>,
        }
        : isPerFlat
          ? {
            tone: 'neutral',
            text: `Per-flat plan: ${subscription.flat_count ?? 0} of ${subscription.flat_limit} flats used.${
              flatUnitAmount !== null ? ` Each extra flat is £${(flatUnitAmount / 100).toFixed(2)} / month, charged now.` : ''}`,
            action: (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => setFixedPlanDialogOpen(true)}>Buy a plan</Button>
                <Button variant="contained" onClick={() => setManageFlatsOpen(true)}>Manage flats</Button>
              </Box>
            ),
          }
        : scheduledCancellationDate
          ? {
            tone: 'neutral',
            text: `Your subscription is scheduled to cancel on ${scheduledCancellationDate}.`,
            action: <Button variant="outlined" onClick={openBillingPortal} disabled={portalLoading}>Manage billing</Button>,
          }
          : null;

  const bannerColor = banner ? { warning: t.warning, danger: t.error, neutral: t.mutedSoft }[banner.tone] : t.hairline;

  const emailLabel = googleEmail && profile && googleEmail.toLowerCase() !== profile.email.toLowerCase() ? 'Sign-in email' : 'Email';
  const displayName = capitalise(profile?.name || '');

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading && !profile) {
    return (
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }} aria-busy="true">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography component="h1" sx={srOnly}>Account settings</Typography>
          <Skeleton variant="rounded" height={88} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Skeleton variant="rounded" height={220} />
            <Skeleton variant="rounded" height={220} />
          </Box>
          <Skeleton variant="rounded" height={150} />
        </Box>
      </Box>
    );
  }

  if (loadError || !profile) {
    return (
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
        <Box>
          <Typography component="h1" sx={srOnly}>Account settings</Typography>
          <Card elevation={0} sx={{ border: `1px solid ${t.hairline}` }}>
            <CardContent>
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchProfile}>Retry</Button>}>
                {loadError || 'We could not load your profile.'}
              </Alert>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const mfaEnabled = Boolean(profile.mfa_enabled);

  return (
    <Box sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography component="h1" sx={srOnly}>Account settings</Typography>
        <Box role="status" aria-live="polite" sx={srOnly}>{announcement}</Box>

        {/* 1. Identity header */}
        <Card elevation={0} sx={{ border: `1px solid ${t.hairline}` }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: t.primary, fontSize: '1.5rem' }} aria-hidden>
                {displayName.charAt(0)}
              </Avatar>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography noWrap title={displayName} sx={{ fontWeight: 600, fontSize: '1.35rem', color: t.ink }}>{displayName}</Typography>
                <Typography noWrap title={`${profile.email} · Member since ${formatDate(profile.created_at)}`} sx={{ fontSize: '15px', color: t.muted }}>
                  {profile.email} · Member since {formatDate(profile.created_at)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                {planBadge && <StatusPill large tone={planBadge.tone} label={planBadge.label} />}
                {canManageSubscription && (
                  <Link component="button" type="button" underline="hover" onClick={openBillingPortal} disabled={portalLoading} sx={{ fontSize: '13px', color: t.muted }}>
                    Manage billing
                  </Link>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 2. Billing notice */}
        {banner && (
          <Box
            role="region"
            aria-label="Billing notice"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25, borderRadius: '8px',
              bgcolor: alpha(bannerColor, 0.1), border: `1px solid ${alpha(bannerColor, 0.4)}`,
            }}
          >
            <CreditCardIcon aria-hidden sx={{ fontSize: 20, color: bannerColor }} />
            <Typography sx={{ flex: '1 1 240px', fontSize: '15px', color: t.ink }}>{banner.text}</Typography>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' }, minHeight: 40, textTransform: 'none' } }}>
              {banner.action}
            </Box>
          </Box>
        )}

        {/* 3. Account and Connected accounts, half the width each */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'stretch' }}>
            {/* Account */}
            <SectionCard title="Account">
              <SettingsRow
                first
                label="Full name"
                action={!editingName && (
                  <Tooltip title="Edit name">
                    <IconButton size="small" aria-label="Edit name" onClick={startEditingName}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              >
                {editingName ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={nameValue}
                        onChange={e => setNameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') cancelEditingName(); }}
                        autoFocus
                        disabled={savingName}
                        error={Boolean(nameError)}
                        inputProps={{ 'aria-label': 'Full name', maxLength: 100 }}
                      />
                      <IconButton size="small" aria-label="Save name" onClick={handleSaveName} disabled={savingName} sx={{ color: t.success }}>
                        {savingName ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" aria-label="Cancel editing name" onClick={cancelEditingName} disabled={savingName}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {nameError && <Typography role="alert" sx={{ fontSize: '12px', color: t.error, mt: 0.5 }}>{nameError}</Typography>}
                  </Box>
                ) : (
                  <Typography noWrap title={profile.name} sx={{ fontSize: '16px', color: t.ink }}>{profile.name}</Typography>
                )}
              </SettingsRow>
              <SettingsRow
                label="I am a"
                action={!editingRole && (
                  <Tooltip title="Edit role">
                    <IconButton size="small" aria-label="Edit role" onClick={() => setEditingRole(true)}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              >
                {editingRole ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      autoFocus
                      value={profile.user_role || ''}
                      disabled={savingRole}
                      onChange={e => handleSaveRole(e.target.value)}
                      inputProps={{ 'aria-label': 'Your role' }}
                    >
                      {USER_ROLES.map(r => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
                    </TextField>
                    <IconButton size="small" aria-label="Cancel editing role" onClick={() => setEditingRole(false)} disabled={savingRole}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '16px', color: profile.user_role ? t.ink : t.mutedSoft }}>
                    {USER_ROLES.find(r => r.id === profile.user_role)?.label || 'Not set'}
                  </Typography>
                )}
              </SettingsRow>
              <SettingsRow label={emailLabel}>
                <Typography noWrap title={profile.email} sx={{ fontSize: '16px', color: t.ink }}>{profile.email}</Typography>
              </SettingsRow>
              <SettingsRow
                label="Password"
                action={<Button size="small" variant="outlined" onClick={() => setPasswordDialogOpen(true)} sx={{ textTransform: 'none' }}>Change</Button>}
              >
                <Typography aria-label="Password hidden" sx={{ fontSize: '16px', color: t.ink, letterSpacing: '0.15em' }}>••••••••</Typography>
              </SettingsRow>
            </SectionCard>

            {/* Connected accounts */}
            <GoogleIntegrationCard
              allowPrimary={!addPaymentPrimary}
              onGoogleEmailChange={setGoogleEmail}
              onAnnounce={announce}
            />
        </Box>

            {/* Security, full width below */}
            <SectionCard title="Security">
              {mfaError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaError(null)}>
                  {mfaError}
                </Alert>
              )}

              {mfaQrCode ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Step 1 of 2: add MillOS to your authenticator app
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Open an authenticator app on your phone. We'd recommend Microsoft Authenticator, but Google Authenticator or Authy work too.
                    {' '}Don't have one yet?{' '}
                    <Link href="https://apps.apple.com/app/microsoft-authenticator/id983156458" target="_blank" rel="noopener noreferrer">Get it for iPhone</Link>
                    {' '}or{' '}
                    <Link href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noopener noreferrer">Android</Link>.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    In the app, tap <strong>Add account</strong> (usually a + button), then scan the QR code below.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <img src={mfaQrCode} alt="Two-factor QR code" style={{ width: 160, height: 160, border: `1px solid ${t.hairline}`, borderRadius: 8 }} />
                    <Typography variant="body2" color="text.secondary">
                      Can't scan it? Type this code into the app instead:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>{mfaSecret}</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Step 2 of 2: confirm it's connected</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Open the new <strong>MillOS</strong> entry in your authenticator app and type the 6-digit code it's showing below.
                  </Typography>
                  <TextField
                    size="small"
                    label="6-digit code"
                    value={mfaConfirmCode}
                    onChange={e => setMfaConfirmCode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleConfirmMfa(); }}
                    disabled={mfaConfirming}
                    sx={{ maxWidth: 180, mb: 2, display: 'block' }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleConfirmMfa}
                    disabled={mfaConfirming || !mfaConfirmCode.trim()}
                    startIcon={mfaConfirming ? <CircularProgress size={16} /> : undefined}
                    sx={{ textTransform: 'none' }}
                  >
                    {mfaConfirming ? 'Confirming' : 'Confirm'}
                  </Button>
                </Box>
              ) : mfaEnabled ? (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <VerifiedUserIcon aria-hidden sx={{ color: t.success, mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: t.ink }}>Two-factor on</Typography>
                    <Typography sx={{ fontSize: '14px', color: t.muted, mb: 1 }}>
                      {profile.mfa_enrolled_at ? (
                        <>Enabled <Tooltip title={formatDate(profile.mfa_enrolled_at, true)}><span>{formatDate(profile.mfa_enrolled_at)}</span></Tooltip>. </>
                      ) : 'Enabled. '}
                      Required to connect banks.
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => { setMfaError(null); setMfaResetDialogOpen(true); }}
                      sx={{ fontSize: '13px', textAlign: 'left' }}
                    >
                      Lost your device? Reset 2FA
                    </Link>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <ShieldIcon aria-hidden sx={{ color: t.warning, mt: 0.25 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: t.ink }}>Two-factor off</Typography>
                    <Typography sx={{ fontSize: '14px', color: t.muted, mb: 1.5 }}>
                      Required before you can connect a bank account. Uses an authenticator app such as Google Authenticator or Authy.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleStartMfaEnroll()}
                      disabled={mfaEnrolling}
                      startIcon={mfaEnrolling ? <CircularProgress size={14} /> : undefined}
                      sx={{ textTransform: 'none' }}
                    >
                      {mfaEnrolling ? 'Starting' : 'Set up two-factor'}
                    </Button>
                  </Box>
                </Box>
              )}
            </SectionCard>

            {/* Delete account */}
            <SectionCard title="Delete account">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ flex: '1 1 320px', fontSize: '14px', color: t.muted }}>
                  Permanently delete your account and everything in it, including certificates, reminders, providers, tenants, invoices, bank connections and uploaded files. This cannot be undone.
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Delete account
                </Button>
              </Box>
            </SectionCard>
      </Box>

      <PaymentDialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} onPaid={() => announce('Plan purchased')} />
      <PaymentDialog fixedPlans open={fixedPlanDialogOpen} onClose={() => setFixedPlanDialogOpen(false)} onPaid={() => announce('Plan purchased')} />
      <ManageFlatsDialog
        open={manageFlatsOpen}
        onClose={() => setManageFlatsOpen(false)}
        flatLimit={subscription?.flat_limit ?? 0}
        onChanged={async () => { await refreshSubscriptionStatus(); announce('Plan updated'); }}
      />

      {/* Delete account */}
      <Dialog open={deleteDialogOpen} onClose={deletingAccount ? undefined : closeDeleteDialog} maxWidth="xs" fullWidth>
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (deleteConfirmed && deletePassword && !deletingAccount) handleDeleteAccount(); }}>
          <DialogTitle>Delete your account?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1.5 }}>
              This permanently deletes your account and all of its data: certificates, reminders, calendar events, providers, tenants, invoices, bank connections and uploaded files. Any paid plan is cancelled. You can't get any of it back.
            </DialogContentText>
            {deleteError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDeleteError(null)}>{deleteError}</Alert>}
            <FormControlLabel
              control={<Checkbox checked={deleteConfirmed} onChange={e => setDeleteConfirmed(e.target.checked)} disabled={deletingAccount} />}
              label="I understand this is permanent and cannot be undone"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth type="password" label="Enter your password to confirm" margin="dense"
              value={deletePassword} onChange={e => setDeletePassword(e.target.value)} disabled={deletingAccount}
              autoComplete="current-password"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={deletingAccount}>Cancel</Button>
            <Button
              type="submit" variant="contained" color="error"
              disabled={!deleteConfirmed || !deletePassword || deletingAccount}
              startIcon={deletingAccount ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {deletingAccount ? 'Deleting' : 'Delete my account'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Change password */}
      <Dialog open={passwordDialogOpen} onClose={changingPassword ? undefined : closePasswordDialog} maxWidth="xs" fullWidth>
        <Box component="form" onSubmit={handleChangePassword}>
          <DialogTitle>Change password</DialogTitle>
          <DialogContent>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPasswordError(null)}>{passwordError}</Alert>
            )}
            <TextField
              autoFocus fullWidth type="password" label="Current password" margin="normal" required
              value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} disabled={changingPassword}
            />
            <TextField
              fullWidth type="password" label="New password" margin="normal" required
              value={newPassword} onChange={e => setNewPassword(e.target.value)} disabled={changingPassword}
              helperText="Must be at least 8 characters"
            />
            <TextField
              fullWidth type="password" label="Confirm new password" margin="normal" required
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} disabled={changingPassword}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closePasswordDialog} disabled={changingPassword}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={changingPassword} startIcon={changingPassword ? <CircularProgress size={16} /> : <SaveIcon />}>
              {changingPassword ? 'Changing' : 'Change password'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Reset two-factor */}
      <Dialog open={mfaResetDialogOpen} onClose={() => { setMfaResetDialogOpen(false); setMfaResetPassword(''); setMfaError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Reset two-factor authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirm your password to generate a new QR code. Your old authenticator app will stop working immediately.
          </Typography>
          {mfaError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaError(null)}>{mfaError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Current password"
            value={mfaResetPassword}
            onChange={e => setMfaResetPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleConfirmResetPassword(); }}
            disabled={mfaEnrolling}
            helperText="This is the password you use to log in to the app, not a code from your authenticator."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMfaResetDialogOpen(false); setMfaResetPassword(''); setMfaError(null); }} disabled={mfaEnrolling}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmResetPassword}
            disabled={mfaEnrolling || !mfaResetPassword.trim()}
            startIcon={mfaEnrolling ? <CircularProgress size={16} /> : undefined}
          >
            {mfaEnrolling ? 'Verifying' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ProfilePage;
